import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renders the SMB Project ID as a clickable monospace pill.
 *
 *  - Visible label is the customer-facing short code (last 8 chars of
 *    the UUID, upper-cased) — same value that gets sent in WhatsApp
 *    template #2 ("Details received! Project ID *2C3D479F* …").
 *  - Hover/title tooltip shows the full UUID for ops who need it.
 *  - Click copies the SHORT code to clipboard (the form customers
 *    actually quote back to ops).
 *
 * Shift-click copies the FULL UUID instead — handy for internal lookups
 * via Mongo console etc.
 */
export const ProjectIdBadge = ({ fullId, className = '' }) => {
  const [copied, setCopied] = useState(false);
  if (!fullId) return null;
  const short = String(fullId).slice(-8).toUpperCase();

  const handleCopy = async (e) => {
    const value = e.shiftKey ? fullId : short;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(
        e.shiftKey
          ? `Full project ID copied (${fullId.slice(0, 8)}…)`
          : `Project ID ${short} copied`,
        { duration: 1800 },
      );
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Project ID: ${fullId}\nClick to copy short code · Shift+Click for full UUID`}
      data-testid="project-id-badge"
      data-project-id={fullId}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 font-mono text-xs hover:bg-emerald-500/20 transition-colors ${className}`}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-60">PID</span>
      <span className="font-semibold">{short}</span>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
};
